import type { ReactNode } from 'react';
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Container,
  Heading,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import PageMeta, { type ArticleMeta, SITE_URL } from './PageMeta';

type Crumb = { name: string; path: string };

export function breadcrumbSchema(crumbs: Crumb[]) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.path}`,
    })),
  };
}

/**
 * Branded content shell for marketing pages (About, Contact).
 * Landing-style hero glow + Big Shoulders H1 — not the legal-doc layout.
 */
export default function MarketingPageShell({
  fullTitle,
  description,
  schema,
  breadcrumbs,
  h1,
  h1Accent,
  lead,
  canonicalUrl,
  article,
  compactHero = false,
  children,
}: {
  fullTitle: string;
  description: string;
  schema: Record<string, unknown> | Record<string, unknown>[];
  breadcrumbs: Crumb[];
  h1: string;
  h1Accent?: string;
  lead: string;
  canonicalUrl?: string;
  article?: ArticleMeta;
  /** Tighter hero for data-heavy pages (e.g. full ratings leaderboard). */
  compactHero?: boolean;
  children: ReactNode;
}) {
  return (
    <Box minH="100vh" bg="gray.900" overflowX="hidden">
      <PageMeta
        fullTitle={fullTitle}
        description={description}
        schema={schema}
        canonicalUrl={canonicalUrl}
        article={article}
      />
      <Navbar />
      <Box
        as="main"
        pt={compactHero ? { base: 6, md: 10 } : { base: 10, md: 16 }}
        pb={compactHero ? { base: 10, md: 16 } : { base: 16, md: 24 }}
        position="relative"
        overflowX="hidden"
      >
        <Box
          position="absolute"
          top="-280px"
          left="50%"
          transform="translateX(-50%)"
          w="1100px"
          maxW="100%"
          h="900px"
          bg="radial-gradient(ellipse at center, rgba(10,112,185,0.2) 0%, rgba(10,112,185,0.06) 42%, transparent 70%)"
          pointerEvents="none"
          zIndex={0}
          aria-hidden
        />
        <Container maxW="container.content" position="relative" zIndex={1}>
          <VStack
            spacing={compactHero ? { base: 4, md: 6 } : { base: 10, md: 14 }}
            align="stretch"
          >
            <VStack spacing={compactHero ? 3 : 5} align="start" maxW="780px">
              <Breadcrumb
                fontSize="sm"
                color="gray.500"
                separator="/"
                sx={{ ol: { flexWrap: 'wrap' } }}
              >
                {breadcrumbs.map((crumb, index) => {
                  const isLast = index === breadcrumbs.length - 1;
                  return (
                    <BreadcrumbItem key={crumb.path} isCurrentPage={isLast}>
                      {isLast ? (
                        <BreadcrumbLink
                          as="span"
                          color="gray.400"
                          cursor="default"
                          _hover={{ textDecoration: 'none' }}
                        >
                          {crumb.name}
                        </BreadcrumbLink>
                      ) : (
                        <BreadcrumbLink
                          as={RouterLink}
                          to={crumb.path}
                          color="gray.500"
                          _hover={{ color: 'primary.400' }}
                        >
                          {crumb.name}
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  );
                })}
              </Breadcrumb>
              <Heading
                as="h1"
                fontSize={
                  compactHero
                    ? { base: '2xl', sm: '3xl', md: '4xl' }
                    : { base: '3xl', sm: '4xl', md: '5xl' }
                }
                fontWeight="extrabold"
                lineHeight="1.1"
                color="white"
                letterSpacing="-0.02em"
              >
                {h1}
                {h1Accent ? (
                  <>
                    {' '}
                    <Text as="span" color="primary.400">
                      {h1Accent}
                    </Text>
                  </>
                ) : null}
              </Heading>
              <Text
                fontSize={compactHero ? { base: 'sm', md: 'md' } : { base: 'md', md: 'xl' }}
                color="gray.400"
                lineHeight={compactHero ? '1.55' : '1.7'}
                maxW={compactHero ? '720px' : '640px'}
              >
                {lead}
              </Text>
            </VStack>
            {children}
          </VStack>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
}
